import { Button, Tooltip, Zoom } from "@mui/material";
import copy from "copy-to-clipboard";
import { useState } from "react";
import { toast } from "react-toastify";

import { ACTION_CATEGORY, ACTION_NAME, ACTION_OBJECT } from "@/constants";
import { ShareCopyIcon } from "@/icons";
import { ActionService } from "@/services";
import { sendMediaShare, sendShare } from "@/utils/shareUtils";

export default function ShareButtons(props) {
  const [copiedLink, setCopiedLink] = useState(false);

  const {
    classes,
    share,
    snackbarOpen,
    isShareLinkLoading,
    setSnackbarOpen,
    propertyId,
    experienceId,
    userId,
    experienceRoutineOnShare,
    onCloseDialog,
    errors,
    experience,
    shareUrl,
    disabled,
  } = props;

  /**
   * Generates a `SHARED` action event when the user copies the share link.
   * This will show up on the experience's last activity table.
   * Will do it only once per opening of the share modal, consecutive clicks won't do anything.
   */
  const createCopyShareAction = async () => {
    if (copiedLink) {
      return;
    }

    setCopiedLink(true);

    await ActionService.addAction({
      name: ACTION_NAME.SHARED,
      category: ACTION_CATEGORY.ENGAGEMENT,
      object: ACTION_OBJECT.EXPERIENCE,
      objectName: experience.title,
      shareUrl,
      experienceId,
      propertyId,
      shareId: +share.id,
      userId: userId || null,
      to: userId ? [userId] : [],
      processed: true, // no need to generate notification if we just copied the link
    });
  };

  const onCopiedActionsSubmit = async () => {
    if (!isShareLinkLoading && share.id) {
      setSnackbarOpen(true);
      copy(shareUrl, {
        format: "text/plain",
      });
      try {
        await experienceRoutineOnShare();
        await createCopyShareAction();
      } catch {
        toast.error("Experience failed to save", {
          containerId: "shareDialog",
        });
      }
    }
  };

  const submitForm = async () => {
    if (errors.title || errors.contactId || (errors.email && errors.phone)) {
      return;
    }

    const { err } = experienceId
      ? await sendShare(propertyId, userId, experienceId, share?.id)
      : await sendMediaShare(propertyId, userId, share?.id);

    await createCopyShareAction();

    if (err) {
      return toast.error("experience failed to share", {
        containerId: "shareDialog",
      });
    } else {
      toast.success("Successfully sent share link", { containerId: "dark" });
    }

    await experienceRoutineOnShare();
    await onCloseDialog();
  };

  const handleMouseLeave = () => {
    setSnackbarOpen(false);
  };

  return (
    <div className={classes.shareButtons} aria-hidden data-testid="share-buttons-container">
      <Tooltip title={snackbarOpen ? "Copied!" : "Copy sharable link"} placement="bottom" TransitionComponent={Zoom}>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          className={classes.shareCopyButton}
          onClick={onCopiedActionsSubmit}
          onMouseLeave={handleMouseLeave}
          startIcon={<ShareCopyIcon />}
          data-testid="share-copy-button"
          disabled={disabled}
        >
          Copy
        </Button>
      </Tooltip>
      <Button
        variant="contained"
        color="primary"
        className={classes.shareButton}
        onClick={() => submitForm()}
        disabled={disabled || errors.title || (errors.email && errors.phone) || errors.contactId}
        data-testid="share-send-button"
      >
        Send
      </Button>
    </div>
  );
}
